import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useTranslation } from "react-i18next";
import { Switch } from "@/components/ui/switch";
import { DayOfWeek } from "@/lib/types";
import { CalendarWeekDaysSubForm } from "@/components/calendar/form/CalendarWeekDaysSubForm";

const formSchema = z.object({
  withCustomWeekDays: z.boolean(),
  weekDays: z.array(
    z.object({
      dayOfWeek: z.nativeEnum(DayOfWeek),
      startTime: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, ""),
      endTime: z.string().regex(/^([0-1][0-9]|2[0-3]):[0-5][0-9]$/, ""),
    })
  ),
});

export type FormSchemaType = z.infer<typeof formSchema>;

type Props = {
  buttonText: string;
  onSubmit: (values: FormSchemaType) => void;
  isLoading: boolean;
  defaultValues?: FormSchemaType;
};

export const CampaignCalendarItemMetaForm: React.FC<Props> = ({ buttonText, onSubmit, isLoading, defaultValues }) => {
  const { t } = useTranslation();

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      withCustomWeekDays: false,
    },
  });

  const withCustomWeekDays = form.watch("withCustomWeekDays");

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="withCustomWeekDays"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("With custom week days")}</FormLabel>
              <FormControl>
                <div>
                  <Switch checked={field.value} onCheckedChange={field.onChange} />
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {withCustomWeekDays && (
          <CalendarWeekDaysSubForm
            form={form as any}
            title={t("campaign.configureWeekDays")}
            description={t("campaign.configureWeekDaysDesc")}
          />
        )}

        <Button type="submit" disabled={isLoading} className="mt-4">
          {buttonText}
          {isLoading && <LoadingSmall />}
        </Button>
      </form>
    </Form>
  );
};
